import { useState } from 'react'
import { useRouter } from 'next/router'

import { useAdFree, useAdFreeTrialStatus } from 'hooks/adFree'
import { useTimeout } from 'hooks/componentDidMount'

import UserService from 'services/user.service'
import ToastService from 'services/toast.service'
import Analytics from 'utils/googleAnalytics'
import Link from 'components/elements/Link'
import PhatButton from 'components/formElements/PhatButton'

import styles from './freeTrailDetails.module.scss'

type Props = {
  onCancel?: () => void
}

const FreeTrialDetails = ({ onCancel }: Props) => {
  const router = useRouter()

  const [adFreeStatus, freeTrialExpiresDate] = useAdFreeTrialStatus()

  const [loading, setLoading] = useState(false)

  const adFree = useAdFree()
  const enableCancel = useTimeout(5000)

  const handleStartTrial = () => {
    setLoading(true)

    UserService.claimAdFreeTrial()
      .then(() => {
        ToastService.create('Ad free accepted - reloading page...', 'User service', 'success')
        Analytics.signalEvent(`Free-trial accepted`)

        setTimeout(() => router.reload(), 1500)
      })
      .catch(error => {
        ToastService.create(error, 'User service', 'error')
        setLoading(false)
      })
  }

  return (
    <>
      <PhatButton onClick={handleStartTrial} disabled={loading} color="orange" className={styles.header}>
        Click Start. Page refreshes, no ads for 30 days. No strings attached.
      </PhatButton>

      {adFreeStatus === 'IN_PROGRESS' && (
        <p>
          You've claimed a 30 day ad-free experience on Archidekt. Your trial will end on{' '}
          {freeTrialExpiresDate.toLocaleDateString()} {freeTrialExpiresDate.toLocaleTimeString()}. At any time, you can
          extend your ad-free experience by supporting us for as little as $2/month on{' '}
          <Link href="https://www.patreon.com/archidekt/membership" className={styles.ctaLink}>
            Patreon
          </Link>
          .
        </p>
      )}

      {adFreeStatus === 'NOT_STARTED' && !adFree && (
        <>
          <p>
            We feel that Archidekt is best experienced when used without ads. We feel so strongly about this that we
            offer all accounts a one-time-use 30 days of ad-free, no strings attached. Clicking the button below will
            start your ad-free trial immediately.
          </p>

          <p>
            At any time, you can extend your ad-free experience by supporting us for as little as $2/month on{' '}
            <Link href="https://www.patreon.com/archidekt/membership" className={styles.ctaLink}>
              Patreon
            </Link>
            . This goes a long way toward helping us to continue to develop Archidekt, so we greatly appreciate your
            contribution!
          </p>

          <p>
            Once you accept the free trial, the floating button at the bottom of the page will go away forever,
            regardless of whether you choose to continue supporting us on Patreon or not.
          </p>
        </>
      )}

      {adFreeStatus === 'EXPIRED' && !adFree && (
        <p>
          You've already used your 30 day ad-free trial. At any time, you can become ad-free again by supporting us for
          as little as $2/month on{' '}
          <Link href="https://www.patreon.com/archidekt/membership" className={styles.ctaLink}>
            Patreon
          </Link>
          .
        </p>
      )}

      {adFreeStatus === 'NOT_STARTED' && !adFree && (
        <div className={styles.controls}>
          {onCancel && (
            <PhatButton disabled={!enableCancel || loading} icon="close" onClick={onCancel}>
              No, thank you
            </PhatButton>
          )}

          <PhatButton icon="arrow circle right" color="green" onClick={handleStartTrial} disabled={loading}>
            {loading ? 'Updating account...' : 'Start 30 day ad-free'}
          </PhatButton>
        </div>
      )}
    </>
  )
}

export default FreeTrialDetails
